import { useState, useEffect, useRef, useCallback } from 'react';
import * as Ably from 'ably';

// ─── Config ───────────────────────────────────────────────────────────────────
// Same-origin API + token-auth Ably (never embed the key) — mirrors TVDisplay.
const API           = process.env.REACT_APP_API_URL || '/api';
const ABLY_AUTH_URL = `${API}/ably/token`;

// How long to hold each active game before rotating to the next (sports-broadcast
// style). We only rotate when more than one game is live.
const ROTATE_MS = 13000;
// Fallback REST poll for the currently-shown game — Ably events are the primary
// signal; this only guards against a missed event, so it can be slow (every
// poll is a Firebase download on the free tier).
const LIVE_POLL_MS = 20000;
// How often to re-discover the set of active games (belt-and-braces alongside the
// admin lifecycle events, which drive instant switches).
const DISCOVER_MS = 60000;

// ─── Game-type presentation metadata ──────────────────────────────────────────
const TYPE_META = {
  crossword:     { label: 'CROSSWORD',      icon: '✏️',  accent: '#00E5FF' },
  americasays:   { label: 'AMERICA SAYS',   icon: '🎯',  accent: '#FFB300' },
  geoguess:      { label: 'GEOGUESS',       icon: '🗺️', accent: '#3DD68C' },
  droppan:       { label: 'DROP PAN',       icon: '🎴',  accent: '#00C853' },
  chainreaction: { label: 'CHAIN REACTION', icon: '🔗',  accent: '#7C4DFF' },
  bracket:       { label: 'BRACKET',        icon: '🏆',  accent: '#F2C94C' },
};
const metaFor = (t) => TYPE_META[t] || { label: (t || 'GAME').toUpperCase(), icon: '🎮', accent: '#FFB300' };

// Per-game REST endpoints (list + single). Crossword's list returns a Firebase
// object keyed by id; the others return arrays — normalised in discoverGames().
const LIST_PATH   = {
  crossword:     `${API}/games`,
  americasays:   `${API}/americasays`,
  geoguess:      `${API}/geoguess`,
  droppan:       `${API}/droppan`,
  chainreaction: `${API}/chainreaction`,
  bracket:       `${API}/bracket`,
};
const SINGLE_PATH = (type, id) => ({
  crossword:     `${API}/games/${id}`,
  americasays:   `${API}/americasays/${id}`,
  geoguess:      `${API}/geoguess/${id}`,
  droppan:       `${API}/droppan/${id}`,
  chainreaction: `${API}/chainreaction/${id}`,
  bracket:       `${API}/bracket/${id}`,
})[type];
// Per-game realtime channel. Crossword's display events ride the `:display`
// sub-channel; every other type publishes score-bearing events on the base channel.
const GAME_CHANNEL = (type, id) => ({
  crossword:     `crossword:game:${id}:display`,
  americasays:   `americasays:game:${id}`,
  geoguess:      `geoguess:game:${id}`,
  droppan:       `droppan:game:${id}`,
  chainreaction: `chainreaction:game:${id}`,
  bracket:       `bracket:game:${id}`,
})[type];

// ─── Shared Ably client (token auth) ──────────────────────────────────────────
let _ablyClient = null;
function getAblyClient() {
  if (!_ablyClient) _ablyClient = new Ably.Realtime({
    authUrl: ABLY_AUTH_URL,
    autoConnect: true,
    webSocketConnectTimeout: 4000,
    disconnectedRetryTimeout: 3000,
  });
  return _ablyClient;
}

// Subscribe to a single channel with a map of { eventName: handler }.
function useAblyChannel(channelName, handlers) {
  const handlersRef = useRef(handlers);
  useEffect(() => { handlersRef.current = handlers; });
  useEffect(() => {
    if (!channelName) return;
    const client  = getAblyClient();
    if (!client) return;
    const channel = client.channels.get(channelName);
    const subs = Object.keys(handlersRef.current).map(event => {
      const cb = (msg) => handlersRef.current[event]?.(msg);
      channel.subscribe(event, cb);
      return { event, cb };
    });
    return () => subs.forEach(({ event, cb }) => channel.unsubscribe(event, cb));
  }, [channelName]);
}

// Subscribe to *every* event on a channel and invoke one handler. Used for the
// per-game channel: the different game engines carry `scores`/`teamScores` under
// many different event names (score-update, answer-revealed, slot-solved,
// round-complete, …), so we simply react whenever a payload contains standings.
function useAblyChannelAll(channelName, handler) {
  const handlerRef = useRef(handler);
  useEffect(() => { handlerRef.current = handler; });
  useEffect(() => {
    if (!channelName) return;
    const client = getAblyClient();
    if (!client) return;
    const channel = client.channels.get(channelName);
    const cb = (msg) => handlerRef.current?.(msg);
    channel.subscribe(cb);
    return () => channel.unsubscribe(cb);
  }, [channelName]);
}

// ─── Score-shape normalisation ────────────────────────────────────────────────
// Every game engine exposes player scores as objects (or a Firebase-keyed map) of
// { name, initials?, score, teamId?, teamColor?, jid? } and team scores as
// { id, name, color, score }. Normalise both to sorted arrays.
function normScores(s) {
  if (!s) return [];
  const arr = Array.isArray(s) ? s : Object.values(s);
  return arr
    .filter(Boolean)
    .map(p => ({ ...p, score: Number(p.score) || 0 }))
    .sort((a, b) => b.score - a.score);
}

// Derive team standings for a game object. chainreaction/droppan already carry an
// explicit teamScores array; crossword/americasays store teams + per-player teamId,
// so aggregate. Returns [] when the game isn't team-based.
function deriveTeamScores(game) {
  if (Array.isArray(game?.teamScores) && game.teamScores.length) return normScores(game.teamScores);
  const teams  = game?.teams;
  const scores = game?.scores;
  if (!Array.isArray(teams) || !teams.length) return [];
  const tm = {};
  teams.forEach(t => { tm[t.id] = { id: t.id, name: t.name, color: t.color, score: 0, playerCount: 0 }; });
  (Array.isArray(scores) ? scores : Object.values(scores || {})).forEach(s => {
    if (s?.teamId && tm[s.teamId]) { tm[s.teamId].score += Number(s.score) || 0; tm[s.teamId].playerCount++; }
  });
  return Object.values(tm).sort((a, b) => b.score - a.score);
}

const teamColorOf = (teamId, teamScores) =>
  teamScores?.find(t => t.id === teamId)?.color || '#00E5FF';

// Round / progress context string per game type (best-effort — degrades to '').
function contextLine(type, game) {
  if (!game) return '';
  if (type === 'geoguess' || type === 'chainreaction') {
    const cur = (typeof game.currentRound === 'number') ? game.currentRound + 1 : null;
    const tot = game.totalRounds || game.rounds?.length || null;
    if (cur && tot) return `ROUND ${cur} / ${tot}`;
    if (cur)        return `ROUND ${cur}`;
  }
  if (type === 'americasays') {
    const cur = (typeof game.currentQ === 'number') ? game.currentQ + 1 : null;
    const tot = game.questions?.length || game.totalQuestions || null;
    if (cur && tot) return `QUESTION ${cur} / ${tot}`;
    if (cur)        return `QUESTION ${cur}`;
  }
  if (type === 'droppan' && game.mode) {
    return ({ dreamnumber: 'DREAM NUMBER', namethemark: 'NAME THE MARK', dreamauction: 'DREAM AUCTION' })[game.mode] || '';
  }
  if (type === 'bracket' && game.phase) {
    return ({ quiz: 'QUIZ ROUND', betting: 'BETTING OPEN' })[game.phase] || '';
  }
  return '';
}

// ─── Count-up number (animates score changes) ─────────────────────────────────
function CountUp({ value, style }) {
  const [display, setDisplay] = useState(value);
  const fromRef = useRef(value);
  const rafRef  = useRef(null);
  useEffect(() => {
    const from = fromRef.current;
    const to   = value;
    if (from === to) { setDisplay(to); return; }
    const start = performance.now();
    const dur   = 600;
    const tick = (now) => {
      const p = Math.min(1, (now - start) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(Math.round(from + (to - from) * eased));
      if (p < 1) rafRef.current = requestAnimationFrame(tick);
      else fromRef.current = to;
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => rafRef.current && cancelAnimationFrame(rafRef.current);
  }, [value]);
  return <span style={style}>{display}</span>;
}

// ─── Animated background ──────────────────────────────────────────────────────
function ScoreboardBg() {
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', inset: 0,
        background: 'radial-gradient(ellipse at 50% 22%, #131038 0%, #0a0818 55%, #020108 100%)' }} />
      <div style={{ position: 'absolute', top: '10%', left: '-20%', right: '-20%', height: '40%',
        background: 'linear-gradient(180deg, transparent, rgba(60,20,140,.20) 40%, rgba(30,100,180,.14) 60%, transparent)',
        transform: 'rotate(-8deg)', filter: 'blur(46px)', animation: 'sbAurora 16s ease infinite' }} />
      <div style={{ position: 'absolute', bottom: '6%', left: '-20%', right: '-20%', height: '30%',
        background: 'linear-gradient(180deg, transparent, rgba(0,140,200,.10) 50%, transparent)',
        transform: 'rotate(5deg)', filter: 'blur(54px)', animation: 'sbAurora 22s ease infinite 6s' }} />
      <div style={{ position: 'absolute', width: '85vw', height: '85vw', top: '-32%', left: '-28%',
        background: 'radial-gradient(circle, rgba(255,100,0,.05) 0%, transparent 60%)',
        animation: 'sbOrb1 24s ease infinite' }} />
      <div style={{ position: 'absolute', width: '75vw', height: '75vw', bottom: '-28%', right: '-24%',
        background: 'radial-gradient(circle, rgba(100,50,220,.055) 0%, transparent 60%)',
        animation: 'sbOrb2 28s ease infinite 5s' }} />
      <div style={{ position: 'absolute', inset: 0,
        backgroundImage: 'linear-gradient(rgba(255,255,255,.015) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.015) 1px,transparent 1px)',
        backgroundSize: '78px 78px' }} />
      <div style={{ position: 'absolute', top: '30%', left: 0, right: 0, height: 1,
        background: 'linear-gradient(90deg,transparent,rgba(255,179,0,.08),rgba(0,200,220,.06),transparent)',
        animation: 'sbSweep 18s linear infinite' }} />
      <div style={{ position: 'absolute', top: '68%', left: 0, right: 0, height: 1,
        background: 'linear-gradient(90deg,transparent,rgba(180,60,220,.06),rgba(0,180,255,.04),transparent)',
        animation: 'sbSweep 24s linear infinite 8s' }} />
    </div>
  );
}

// ─── Live clock + on-air indicator ────────────────────────────────────────────
function LiveClock() {
  const [t, setT] = useState(() => new Date());
  useEffect(() => { const iv = setInterval(() => setT(new Date()), 1000); return () => clearInterval(iv); }, []);
  const hh = String(t.getHours()).padStart(2, '0');
  const mm = String(t.getMinutes()).padStart(2, '0');
  const ss = String(t.getSeconds()).padStart(2, '0');
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'clamp(10px,1.4vw,20px)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8,
        background: 'rgba(255,45,60,.14)', border: '1px solid rgba(255,45,60,.5)',
        borderRadius: 6, padding: 'clamp(5px,.7vh,9px) clamp(10px,1.1vw,16px)' }}>
        <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#FF2D3C',
          boxShadow: '0 0 12px #FF2D3C', animation: 'sbBlink 1.4s ease infinite' }} />
        <span style={{ fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 800,
          letterSpacing: 3, fontSize: 'clamp(13px,1.3vw,18px)', color: '#fff' }}>LIVE</span>
      </div>
      <div style={{ fontFamily: "'Bebas Neue',sans-serif", letterSpacing: 3,
        fontSize: 'clamp(20px,2vw,30px)', color: 'rgba(255,255,255,.85)',
        fontVariantNumeric: 'tabular-nums' }}>
        {hh}:{mm}<span style={{ opacity: .5, fontSize: '.7em' }}>:{ss}</span>
      </div>
    </div>
  );
}

// ─── Broadcast header bar ─────────────────────────────────────────────────────
function HeaderBar({ orgName }) {
  return (
    <div style={{
      position: 'relative', zIndex: 20,
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: 'clamp(14px,2vh,26px) clamp(24px,3vw,52px)',
      borderBottom: '1px solid rgba(255,179,0,.14)',
      background: 'linear-gradient(180deg, rgba(4,4,14,.72) 0%, rgba(4,4,14,0) 100%)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'clamp(12px,1.4vw,20px)' }}>
        <div style={{
          width: 'clamp(46px,4vw,62px)', height: 'clamp(46px,4vw,62px)', borderRadius: 16,
          background: 'linear-gradient(135deg,#FF8C00,#FFB300)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 'clamp(24px,2.4vw,34px)', boxShadow: '0 0 34px rgba(255,140,0,.5)' }}>🎯</div>
        <div>
          <div style={{ fontFamily: "'Bebas Neue',sans-serif", letterSpacing: 4,
            fontSize: 'clamp(26px,2.8vw,44px)', color: '#fff', lineHeight: .95,
            textShadow: '0 0 30px rgba(255,179,0,.4)' }}>
            LIVE SCOREBOARD
          </div>
          <div style={{ fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700,
            letterSpacing: 5, fontSize: 'clamp(11px,1.1vw,15px)', color: 'rgba(255,179,0,.65)' }}>
            {orgName ? orgName.toUpperCase() : 'GAME SHOW NETWORK'}
          </div>
        </div>
      </div>
      <LiveClock />
    </div>
  );
}

// ─── Lower-third ticker ───────────────────────────────────────────────────────
function Ticker({ items }) {
  const text = (items && items.length ? items : ['LIVE MULTIPLAYER GAME SHOW', 'SEND YOUR ANSWERS VIA WHATSAPP'])
    .join('     •     ');
  return (
    <div style={{
      position: 'relative', zIndex: 20,
      display: 'flex', alignItems: 'stretch',
      borderTop: '1px solid rgba(255,179,0,.18)',
      background: 'linear-gradient(0deg, rgba(4,4,14,.85) 0%, rgba(4,4,14,.35) 100%)',
      height: 'clamp(38px,5vh,58px)', overflow: 'hidden',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', padding: '0 clamp(16px,1.8vw,28px)',
        background: 'linear-gradient(135deg,#FF8C00,#FFB300)', color: '#1a0800',
        fontFamily: "'Bebas Neue',sans-serif", letterSpacing: 3,
        fontSize: 'clamp(15px,1.5vw,22px)', flexShrink: 0 }}>
        📡 ON AIR
      </div>
      <div style={{ flex: 1, overflow: 'hidden', display: 'flex', alignItems: 'center' }}>
        <div style={{ whiteSpace: 'nowrap', animation: 'sbTicker 34s linear infinite',
          fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700, letterSpacing: 2,
          fontSize: 'clamp(14px,1.5vw,22px)', color: 'rgba(255,255,255,.8)', paddingLeft: '100%' }}>
          {text}     •     {text}
        </div>
      </div>
    </div>
  );
}

// ─── Standings row ────────────────────────────────────────────────────────────
const MEDALS = ['🥇', '🥈', '🥉'];
const RANK_COLOR = (r) => r === 0 ? '#FFD700' : r === 1 ? '#C0C0C0' : r === 2 ? '#CD7F32' : 'rgba(255,255,255,.45)';

function StandingRow({ entry, rank, accent, chip, big }) {
  const leading = rank === 0;
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 'clamp(12px,1.4vw,22px)',
      padding: big ? 'clamp(12px,1.7vh,22px) clamp(16px,1.8vw,28px)' : 'clamp(8px,1.1vh,14px) clamp(14px,1.6vw,22px)',
      borderRadius: 14,
      background: leading
        ? 'linear-gradient(90deg, rgba(255,215,0,.16), rgba(255,215,0,.03))'
        : 'rgba(255,255,255,.035)',
      border: `1px solid ${leading ? 'rgba(255,215,0,.32)' : 'rgba(255,255,255,.06)'}`,
      boxShadow: leading ? '0 0 34px rgba(255,180,0,.14)' : 'none',
      transition: 'background .4s ease, border-color .4s ease',
      opacity: entry.left ? .4 : 1,
    }}>
      <span style={{
        minWidth: big ? 'clamp(40px,3.4vw,58px)' : 'clamp(30px,2.6vw,44px)', textAlign: 'center',
        fontFamily: "'Bebas Neue',sans-serif", fontWeight: 700,
        fontSize: big ? 'clamp(24px,2.8vw,42px)' : 'clamp(18px,2vw,30px)',
        color: RANK_COLOR(rank), lineHeight: 1 }}>
        {MEDALS[rank] || (rank + 1)}
      </span>
      {chip && (
        <span style={{ width: 'clamp(6px,.5vw,9px)', alignSelf: 'stretch', minHeight: 22,
          borderRadius: 4, background: chip, boxShadow: `0 0 10px ${chip}99`, flexShrink: 0 }} />
      )}
      <span style={{ flex: 1, minWidth: 0, fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700,
        fontSize: big ? 'clamp(20px,2.4vw,38px)' : 'clamp(16px,1.9vw,28px)', color: '#fff',
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        textShadow: leading ? '0 0 20px rgba(255,215,0,.35)' : 'none' }}>
        {entry.name || entry.initials || '—'}
      </span>
      <CountUp value={entry.score} style={{
        fontFamily: "'Bebas Neue',sans-serif", letterSpacing: 1,
        fontSize: big ? 'clamp(26px,3vw,46px)' : 'clamp(20px,2.3vw,34px)',
        color: leading ? '#FFD700' : accent, minWidth: big ? 'clamp(70px,7vw,120px)' : 'clamp(56px,5vw,90px)',
        textAlign: 'right', fontVariantNumeric: 'tabular-nums',
        textShadow: leading ? '0 0 22px rgba(255,215,0,.6)' : `0 0 14px ${accent}55` }} />
    </div>
  );
}

// Animated list: rows keyed by identity so React reorders them; a wrapping
// transition on transform gives the FLIP-like slide as ranks change.
function StandingsList({ rows, keyOf, accent, chip, big, emptyLabel }) {
  if (!rows.length) {
    return (
      <div style={{ textAlign: 'center', padding: 'clamp(24px,4vh,48px)',
        color: 'rgba(255,255,255,.28)', fontFamily: "'Barlow Condensed',sans-serif",
        letterSpacing: 3, fontWeight: 600, fontSize: 'clamp(15px,1.6vw,22px)' }}>
        {emptyLabel || 'NO SCORES YET'}
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: big ? 'clamp(8px,1.1vh,14px)' : 'clamp(6px,.8vh,10px)' }}>
      {rows.map((entry, rank) => (
        <div key={keyOf(entry, rank)} style={{ animation: 'sbRowIn .45s cubic-bezier(.34,1.4,.64,1) both' }}>
          <StandingRow entry={entry} rank={rank} accent={accent}
            chip={chip ? chip(entry) : null} big={big} />
        </div>
      ))}
    </div>
  );
}

// ─── Game scoreboard card (one active match) ──────────────────────────────────
function GameScoreCard({ state }) {
  const { type, game, scores, teamScores } = state;
  const meta   = metaFor(type);
  const ctx    = contextLine(type, game);
  const hasTeams = teamScores.length > 0;
  const players  = scores.slice(0, hasTeams ? 8 : 10);

  return (
    <div style={{
      position: 'relative', zIndex: 10, flex: 1, minHeight: 0,
      display: 'flex', flexDirection: 'column',
      padding: 'clamp(14px,2.4vh,34px) clamp(24px,4vw,72px)',
      animation: 'sbCardIn .5s ease both',
    }}>
      {/* Match title strip */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 'clamp(12px,1.6vw,22px)',
        marginBottom: 'clamp(12px,2vh,24px)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10,
          padding: 'clamp(7px,1vh,12px) clamp(14px,1.6vw,24px)', borderRadius: 12,
          background: `${meta.accent}1f`, border: `1px solid ${meta.accent}66` }}>
          <span style={{ fontSize: 'clamp(20px,2.2vw,32px)' }}>{meta.icon}</span>
          <span style={{ fontFamily: "'Bebas Neue',sans-serif", letterSpacing: 3,
            fontSize: 'clamp(18px,2vw,30px)', color: meta.accent }}>{meta.label}</span>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 800,
            fontSize: 'clamp(22px,2.6vw,42px)', color: '#fff', lineHeight: 1,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {game?.name || meta.label}
          </div>
          {ctx && (
            <div style={{ fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700,
              letterSpacing: 4, fontSize: 'clamp(11px,1.2vw,16px)', color: 'rgba(255,179,0,.6)',
              marginTop: 4 }}>{ctx}</div>
          )}
        </div>
        <div style={{ fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700,
          letterSpacing: 3, fontSize: 'clamp(11px,1.1vw,15px)', color: 'rgba(255,255,255,.35)',
          textAlign: 'right', flexShrink: 0 }}>
          {(scores.length || 0)} PLAYER{scores.length === 1 ? '' : 'S'}
        </div>
      </div>

      {/* Standings — teams (left) + players (right) when team-based, else players full width */}
      <div style={{ flex: 1, minHeight: 0, display: 'grid',
        gridTemplateColumns: hasTeams ? 'minmax(0,1fr) minmax(0,1.15fr)' : 'minmax(0,1fr)',
        gap: 'clamp(18px,2.6vw,44px)', alignContent: 'start' }}>
        {hasTeams && (
          <div>
            <SectionLabel accent={meta.accent}>TEAMS</SectionLabel>
            <StandingsList
              rows={teamScores.slice(0, 6)}
              keyOf={(t, i) => t.id || t.name || i}
              accent={meta.accent}
              chip={(t) => t.color || meta.accent}
              big
            />
          </div>
        )}
        <div>
          <SectionLabel accent={meta.accent}>{hasTeams ? 'TOP PLAYERS' : 'LEADERBOARD'}</SectionLabel>
          <StandingsList
            rows={players}
            keyOf={(p, i) => p.jid || p.name || i}
            accent={meta.accent}
            chip={hasTeams ? (p) => (p.teamColor || teamColorOf(p.teamId, teamScores)) : null}
            big={!hasTeams}
          />
        </div>
      </div>
    </div>
  );
}

function SectionLabel({ children, accent }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 'clamp(8px,1.4vh,16px)' }}>
      <span style={{ width: 'clamp(18px,1.6vw,28px)', height: 3, borderRadius: 2,
        background: accent, boxShadow: `0 0 10px ${accent}` }} />
      <span style={{ fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 800, letterSpacing: 5,
        fontSize: 'clamp(12px,1.2vw,17px)', color: 'rgba(255,255,255,.55)' }}>{children}</span>
    </div>
  );
}

// ─── Standby: org lifetime leaderboard ────────────────────────────────────────
function StandbyCard({ lb, orgName }) {
  const rows = normScores((lb || []).map(p => ({ ...p, score: p.totalScore || 0 }))).slice(0, 10);
  return (
    <div style={{ position: 'relative', zIndex: 10, flex: 1, minHeight: 0,
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      padding: 'clamp(16px,3vh,44px) clamp(24px,5vw,90px)', animation: 'sbCardIn .5s ease both' }}>
      <div style={{ textAlign: 'center', marginBottom: 'clamp(14px,2.4vh,30px)' }}>
        <div style={{ fontSize: 'clamp(48px,6vw,84px)', lineHeight: 1, animation: 'sbBob 3s ease infinite' }}>🏆</div>
        <div style={{ fontFamily: "'Bebas Neue',sans-serif", letterSpacing: 6,
          fontSize: 'clamp(30px,3.6vw,58px)', color: '#fff', marginTop: 8,
          textShadow: '0 0 34px rgba(255,179,0,.4)' }}>
          {orgName ? `${orgName.toUpperCase()} — HALL OF FAME` : 'HALL OF FAME'}
        </div>
        <div style={{ fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700, letterSpacing: 5,
          fontSize: 'clamp(12px,1.3vw,18px)', color: 'rgba(255,179,0,.6)', marginTop: 4 }}>
          AWAITING THE NEXT GAME · CUMULATIVE STANDINGS
        </div>
      </div>
      <div style={{ width: 'min(900px, 92vw)' }}>
        <StandingsList
          rows={rows}
          keyOf={(p, i) => p.initials || p.name || i}
          accent="#FFB300"
          emptyLabel="STANDINGS WILL APPEAR ONCE GAMES ARE PLAYED"
        />
      </div>
    </div>
  );
}

// ─── Rotation dots ────────────────────────────────────────────────────────────
function RotationDots({ count, active }) {
  if (count <= 1) return null;
  return (
    <div style={{ position: 'relative', zIndex: 20, display: 'flex', justifyContent: 'center',
      gap: 10, padding: 'clamp(6px,1vh,12px) 0' }}>
      {Array.from({ length: count }, (_, i) => (
        <span key={i} style={{
          width: i === active ? 28 : 9, height: 9, borderRadius: 6,
          background: i === active ? '#FFB300' : 'rgba(255,255,255,.22)',
          boxShadow: i === active ? '0 0 12px rgba(255,179,0,.7)' : 'none',
          transition: 'width .35s ease, background .35s ease' }} />
      ))}
    </div>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────
const SB_STYLES = `
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  html, body, #root { height: 100dvh !important; max-height: 100dvh !important; overflow: hidden !important; }
  ::-webkit-scrollbar { width: 0 !important; height: 0 !important; display: none !important; }
  * { scrollbar-width: none !important; }

  @keyframes sbAurora { 0%,100%{opacity:.12;transform:scaleX(1)} 50%{opacity:.26;transform:scaleX(1.06)} }
  @keyframes sbOrb1   { 0%,100%{transform:translate(0,0) scale(1)} 50%{transform:translate(50px,-40px) scale(1.15)} }
  @keyframes sbOrb2   { 0%,100%{transform:translate(0,0) scale(1)} 50%{transform:translate(-60px,30px) scale(1.1)} }
  @keyframes sbSweep  { from{transform:translateX(-120%)} to{transform:translateX(120vw)} }
  @keyframes sbBlink  { 0%,100%{opacity:1} 50%{opacity:.25} }
  @keyframes sbTicker { from{transform:translateX(0)} to{transform:translateX(-50%)} }
  @keyframes sbRowIn  { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
  @keyframes sbCardIn { from{opacity:0;transform:translateY(16px) scale(.995)} to{opacity:1;transform:translateY(0) scale(1)} }
  @keyframes sbBob    { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-10px)} }
  @keyframes sbFade   { from{opacity:0} to{opacity:1} }
`;

// ─── Main component ───────────────────────────────────────────────────────────
export default function Scoreboard() {
  // Rotation set of active/waiting games: [{ id, type, name, createdAt, status }]
  const [games, setGames]   = useState([]);
  const [idx, setIdx]       = useState(0);
  // Live state for the game currently on screen.
  const [live, setLive]     = useState(null); // { type, game, scores, teamScores }
  const [orgName, setOrgName] = useState('');
  const [orgLb, setOrgLb]     = useState([]);
  const [fade, setFade]       = useState(false);

  const gamesRef = useRef(games);
  const idxRef   = useRef(idx);
  useEffect(() => { gamesRef.current = games; }, [games]);
  useEffect(() => { idxRef.current = idx; }, [idx]);

  const current = games[idx] || null;

  // ── Discover the set of live games across every type ────────────────────────
  const discoverGames = useCallback(async () => {
    const types = Object.keys(LIST_PATH);
    const results = await Promise.all(types.map(t =>
      fetch(LIST_PATH[t]).then(r => r.json()).catch(() => ({ games: [] }))));
    const all = [];
    types.forEach((t, i) => {
      const raw = results[i]?.games || {};
      const list = Array.isArray(raw) ? raw : Object.values(raw);
      list.forEach(g => {
        if (!g || !g.id) return;
        if (g.status === 'active' || g.status === 'waiting') {
          all.push({ id: g.id, type: t, name: g.name, createdAt: g.createdAt || 0, status: g.status });
        }
      });
    });
    // Active first, then most-recently-created.
    all.sort((a, b) =>
      (a.status === b.status ? 0 : a.status === 'active' ? -1 : 1) ||
      (b.createdAt - a.createdAt));

    setGames(prev => {
      // Preserve current selection if that game is still live to avoid a jump.
      const curId = prev[idxRef.current]?.id;
      const stillThere = all.findIndex(g => g.id === curId);
      if (stillThere >= 0 && stillThere !== idxRef.current) setIdx(stillThere);
      else if (stillThere < 0) setIdx(0);
      return all;
    });
  }, []);

  // ── Fetch full state for a specific game (initial + poll fallback) ──────────
  const loadLive = useCallback(async (entry) => {
    if (!entry) { setLive(null); return; }
    try {
      const g = await fetch(SINGLE_PATH(entry.type, entry.id)).then(r => r.ok ? r.json() : null);
      if (!g) return;
      // If the game vanished / completed, drop it from rotation.
      if (g.status === 'completed' || g.status === 'deleted') { discoverGames(); return; }
      setLive({
        type: entry.type,
        game: { ...g, name: g.name || entry.name },
        scores: normScores(g.scores),
        teamScores: deriveTeamScores(g),
      });
    } catch { /* transient — keep last */ }
  }, [discoverGames]);

  // ── Org leaderboard (standby + org name in header) ──────────────────────────
  const loadOrg = useCallback(() => {
    fetch(`${API}/leaderboard/active`).then(r => r.json()).then(d => {
      setOrgName(d?.org?.name || '');
      setOrgLb(d?.leaderboard || []);
    }).catch(() => {});
  }, []);

  // Initial load
  useEffect(() => { discoverGames(); loadOrg(); }, [discoverGames, loadOrg]);

  // Periodic re-discovery + org refresh
  useEffect(() => {
    const iv = setInterval(discoverGames, DISCOVER_MS);
    const ivOrg = setInterval(loadOrg, 120000); // slow fallback — push is primary
    return () => { clearInterval(iv); clearInterval(ivOrg); };
  }, [discoverGames, loadOrg]);

  // When the on-screen game changes, (re)load its live state immediately.
  useEffect(() => {
    if (current) loadLive(current);
    else setLive(null);
  }, [current?.id, current?.type]); // eslint-disable-line

  // Poll the on-screen game as an Ably fallback.
  useEffect(() => {
    if (!current) return;
    const iv = setInterval(() => loadLive(current), LIVE_POLL_MS);
    return () => clearInterval(iv);
  }, [current?.id, current?.type, loadLive]); // eslint-disable-line

  // ── Auto-rotate between multiple live games ─────────────────────────────────
  useEffect(() => {
    if (games.length <= 1) return;
    const iv = setInterval(() => {
      setFade(true);
      setTimeout(() => {
        setIdx(i => (i + 1) % gamesRef.current.length);
        setFade(false);
      }, 320);
    }, ROTATE_MS);
    return () => clearInterval(iv);
  }, [games.length]);

  // ── Live per-game channel: react to any score-bearing event ─────────────────
  const channel = current ? GAME_CHANNEL(current.type, current.id) : null;
  useAblyChannelAll(channel, (msg) => {
    const d = msg?.data;
    if (!d) return;
    // game-state → completed: pull it out of rotation.
    if (d.state === 'completed' || d.state === 'deleted') { discoverGames(); return; }
    const hasScores = d.scores !== undefined || d.teamScores !== undefined;
    if (!hasScores) return;
    setLive(prev => {
      if (!prev) return prev;
      const nextScores = d.scores !== undefined ? normScores(d.scores) : prev.scores;
      let nextTeams = prev.teamScores;
      if (d.teamScores !== undefined && (Array.isArray(d.teamScores) ? d.teamScores.length : d.teamScores)) {
        nextTeams = normScores(d.teamScores);
      } else if (d.scores !== undefined && prev.game?.teams?.length) {
        // crossword/americasays: recompute team totals from fresh player scores.
        nextTeams = deriveTeamScores({ teams: prev.game.teams, scores: nextScores });
      }
      return { ...prev, scores: nextScores, teamScores: nextTeams };
    });
  });

  // ── Admin lifecycle channel: instant switches ───────────────────────────────
  const onLifecycle = useCallback(() => { discoverGames(); }, [discoverGames]);
  useAblyChannel('crossword:admin', {
    'game-created':          onLifecycle,
    'americasays-created':   onLifecycle,
    'geoguess-created':      onLifecycle,
    'droppan-game-created':  onLifecycle,
    'chainreaction-created': onLifecycle,
    'bracket-game-created':  onLifecycle,
    'game-started':          onLifecycle,
    'game-deleted':          onLifecycle,
    'americasays-deleted':   onLifecycle,
    'geoguess-deleted':      onLifecycle,
    'droppan-deleted':       onLifecycle,
    'chainreaction-deleted': onLifecycle,
    'bracket-deleted':       onLifecycle,
    // Active org switched / new results recorded — refresh the standby
    // Hall-of-Fame + header instantly (no manual reload needed).
    'org-leaderboard-updated': () => loadOrg(),
  });

  // ── Ticker items (leaders across live games) ────────────────────────────────
  const tickerItems = games.length
    ? games.map(g => {
        const m = metaFor(g.type);
        return `${m.label}: ${g.name || 'LIVE'}`;
      })
    : (orgName ? [`${orgName.toUpperCase()} · LIVE MULTIPLAYER GAME SHOW`] : null);

  return (
    <div style={{ position: 'fixed', inset: 0, display: 'flex', flexDirection: 'column',
      fontFamily: "'Barlow',sans-serif", color: '#fff', overflow: 'hidden', background: '#020108' }}>
      <style>{SB_STYLES}</style>
      <ScoreboardBg />
      <HeaderBar orgName={orgName} />

      <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column',
        opacity: fade ? 0 : 1, transition: 'opacity .3s ease' }}>
        {current && live ? (
          <GameScoreCard key={`${current.type}:${current.id}`} state={live} />
        ) : (
          <StandbyCard lb={orgLb} orgName={orgName} />
        )}
      </div>

      <RotationDots count={games.length} active={idx} />
      <Ticker items={tickerItems} />
    </div>
  );
}
